import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Paper, IconButton, Collapse } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";

import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import _ from "lodash";

import { WorkflowNameInput, Dropdown, Input } from "../../components";
import DateRangePicker from "../../components/DateRangePicker";
import { workflowStatuses } from "../../utils/constants";
import { useTabsList } from "../../contexts/TabsContext";

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    padding: "12px",
    zIndex: 0,
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  rowGrid: {
    display: "grid",
    gridGap: "16px",
  },
  row1Grid: {
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  input: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  requestIdField: {
    flex: 1,
    maxWidth: "400px", // Prevents requestId from stretching full width
    minWidth: "200px",
  },
  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },

  buttonBase: {
    height: "36px",
    minHeight: "36px",
    boxSizing: "border-box",
  },
  searchButton: {
    backgroundColor: "#1976d2",
    color: "#fff",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#1565c0",
    },
    padding: "6px 16px",
  },
  resetButton: {
    color: "#1976d2",
    borderColor: "#1976d2",
    fontWeight: 500,
    border: "1px solid #1976d2",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.08)",
    },
    padding: "6px 16px",
  },
  closeButton: {
    color: "#6b6b6b",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.08)",
    },
    padding: 8,
  },
}));

const GlobalSearch = React.memo(
  ({
    visible,
    onClose,
    onSearch,
    initialValues = {
      workflowType: [],
      workflowId: "",
      status: [],
      startFrom: "",
      startTo: "",
      lookback: "",
      freeText: "",
      requestId: "",
    },
  }) => {
    const classes = useStyles();
    const [workflowType, setWorkflowType] = useState([]);
    const [workflowId, setWorkflowId] = useState("");
    const [status, setStatus] = useState([]);
    const [startFrom, setStartFrom] = useState("");
    const [startTo, setStartTo] = useState("");
    const [lookback, setLookback] = useState("");
    const [freeText, setFreeText] = useState("");
    const [requestId, setRequestId] = useState("");
    const { tabsList } = useTabsList();

    const execution_actions = useMemo(
      () => tabsList?.Execution?.workflows?.execution_actions === "True",
      [tabsList]
    );

    useEffect(() => {
      if (visible) {
        setWorkflowType(initialValues.workflowType || []);
        setWorkflowId(initialValues.workflowId || "");
        setStatus(initialValues.status || []);
        setStartFrom(initialValues.startFrom || "");
        setStartTo(initialValues.startTo || "");
        setLookback(initialValues.lookback || "");
        setFreeText(initialValues.freeText || "");
        setRequestId(initialValues.requestId || "");
      }
    }, [visible, initialValues]);

    const handleLookback = useCallback((val) => {
      setStartFrom("");
      setStartTo("");
      setLookback(val);
    }, []);

    const handleStartFrom = useCallback((val) => {
      setLookback("");
      setStartFrom(val);
    }, []);

    const handleStartTo = useCallback((val) => {
      setLookback("");
      setStartTo(val);
    }, []);

    const handleReset = useCallback(() => {
      setWorkflowType([]);
      setWorkflowId("");
      setStatus([]);
      setStartFrom("");
      setStartTo("");
      setLookback("");
      setFreeText("");
      setRequestId("");
    }, []);

    const handleSearch = useCallback(() => {
      onSearch({
        workflowType,
        workflowId,
        status,
        startFrom,
        startTo,
        lookback,
        freeText,
        requestId,
      });
    }, [
      onSearch,
      workflowType,
      workflowId,
      status,
      startFrom,
      startTo,
      lookback,
      freeText,
      requestId,
    ]);

    return (
      <Collapse in={visible}>
        <Paper className={classes.root} elevation={3}>
          {/* Row 1: Grid for four inputs */}
          <div className={`${classes.rowGrid} ${classes.row1Grid}`}>
            {/* ...existing code for WorkflowNameInput, Input, Dropdown, DateRangePicker... */}
            <WorkflowNameInput
              fullWidth
              placeholder="Workflow Name"
              onChange={(evt, val) => setWorkflowType(val)}
              value={workflowType}
            />
            <Input
              label="Workflow ID"
              value={workflowId}
              placeholder="Workflow ID"
              onChange={setWorkflowId}
              clearable
            />
            <Dropdown
              label="Status"
              placeholder="Status"
              options={workflowStatuses}
              multiple
              onChange={(evt, val) => setStatus(val)}
              value={status}
            />
            <DateRangePicker
              disabled={!_.isEmpty(lookback)}
              from={startFrom}
              to={startTo}
              onFromChange={handleStartFrom}
              onToChange={handleStartTo}
            />
          </div>

          {/* Row 2: Grid for fields and button group */}
          <div
            className={classes.rowGrid}
            style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
          >
            {true ? (
              <>
                <div className={classes.input}>
                  <Input
                    label="Lookback (days)"
                    placeholder="Lookback (days)"
                    value={lookback}
                    onChange={handleLookback}
                    type="number"
                    clearable
                    disabled={!_.isEmpty(startFrom) || !_.isEmpty(startTo)}
                  />
                </div>
                <div className={classes.input}>
                  <Input
                    label="Lucene-syntax Query"
                    placeholder="Lucene-syntax Query (Double-quote strings for Free Text Search)"
                    value={freeText}
                    onChange={setFreeText}
                    clearable
                  />
                </div>
                <div className={classes.input}>
                  <Input
                    label="Request ID"
                    placeholder="Request ID"
                    value={requestId}
                    onChange={setRequestId}
                    clearable
                  />
                </div>
                <div className={classes.input}>
                  <div
                    className={classes.actionsRow}
                    style={{ justifyContent: "flex-end" }}
                  >
                    <Button
                      onClick={handleSearch}
                      className={`${classes.buttonBase} ${classes.searchButton}`}
                      variant="contained"
                      startIcon={<SearchIcon />}
                      size="small"
                    >
                      Search
                    </Button>
                    <Button
                      onClick={handleReset}
                      className={`${classes.buttonBase} ${classes.resetButton}`}
                      variant="outlined"
                      startIcon={<RotateLeftIcon />}
                      size="small"
                    >
                      Reset
                    </Button>
                    <IconButton
                      aria-label="close"
                      onClick={onClose}
                      className={`${classes.buttonBase} ${classes.closeButton}`}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={classes.input}>
                  <Input
                    label="Request ID"
                    placeholder="Request ID"
                    value={requestId}
                    onChange={setRequestId}
                    clearable
                  />
                </div>
                <div /> {/* Filler cell */}
                <div /> {/* Filler cell */}
                <div className={classes.input}>
                  <div
                    className={classes.actionsRow}
                    style={{ justifyContent: "flex-end" }}
                  >
                    <Button
                      onClick={handleSearch}
                      className={`${classes.buttonBase} ${classes.searchButton}`}
                      variant="contained"
                      startIcon={<SearchIcon />}
                      size="small"
                    >
                      Search
                    </Button>
                    <Button
                      onClick={handleReset}
                      className={`${classes.buttonBase} ${classes.resetButton}`}
                      variant="outlined"
                      startIcon={<RotateLeftIcon />}
                      size="small"
                    >
                      Reset
                    </Button>
                    <IconButton
                      aria-label="close"
                      onClick={onClose}
                      className={`${classes.buttonBase} ${classes.closeButton}`}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
              </>
            )}
          </div>
        </Paper>
      </Collapse>
    );
  },
  (prevProps, nextProps) =>
    prevProps.visible === nextProps.visible &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.onSearch === nextProps.onSearch &&
    _.isEqual(prevProps.initialValues, nextProps.initialValues)
);

GlobalSearch.displayName = "GlobalSearch";
export default GlobalSearch;
